/* ═══════════════════════════════════════════════════════════════
   SpecMasterService — Spec Master + Draft CRUD via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { SpecMaster, SpecMasterDraft } from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';
import { log as logActivity } from './activityLogger';

const masterOpts: TableOpts = {
  jsonFields: ['coreContent', 'systems', 'variants', 'tags', 'versionHistory', 'editHistory'],
};

const draftOpts: TableOpts = {
  jsonFields: ['coreContent', 'systems', 'variants', 'publishRequest'],
  columnMap: {
    blueprint_id: 'spec_master_id',
  },
};

export const SpecMasterService = {
  /* ── Published Spec Masters ── */
  list: async (): Promise<SpecMaster[]> => cache.list<SpecMaster>('spec_masters', masterOpts),

  get: async (id: string): Promise<SpecMaster | null> => {
    const all = await SpecMasterService.list();
    return all.find((m) => m.id === id) || null;
  },

  save: async (master: SpecMaster): Promise<SpecMaster> => {
    const isNew = !master.id;
    const saved = await cache.save('spec_masters', master, masterOpts);
    logActivity({
      action: isNew ? 'spec.publish' : 'spec.update',
      entityType: 'spec_master',
      entityId: saved.id,
      metadata: { name: (saved as any).name, sectionNumber: (saved as any).sectionNumber },
    });
    return saved;
  },

  delete: async (id: string): Promise<void> => {
    await cache.remove('spec_masters', id);
    logActivity({ action: 'spec.delete', entityType: 'spec_master', entityId: id });
  },

  /* ── Drafts ── */
  listDrafts: async (): Promise<SpecMasterDraft[]> =>
    cache.list<SpecMasterDraft>('spec_master_drafts', draftOpts),

  getDraft: async (id: string): Promise<SpecMasterDraft | null> => {
    const all = await SpecMasterService.listDrafts();
    return all.find((d) => d.id === id) || null;
  },

  saveDraft: async (draft: SpecMasterDraft): Promise<SpecMasterDraft> => {
    const saved = await cache.save('spec_master_drafts', draft, draftOpts);
    logActivity({
      action: 'spec.draft_save',
      entityType: 'spec_master_draft',
      entityId: saved.id,
    });
    return saved;
  },

  deleteDraft: async (id: string): Promise<void> => cache.remove('spec_master_drafts', id),
};
